import React from 'react';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch } from 'react-redux';
import type { IDisclosureOptions, ISendPhoneVerificationCodeOptions } from '../../resources/Server';
import { signerPhoneNumberCollected } from '../../store/actions';
import { EnterContactInfoDialog } from './EnterContactInfoDialog';
import { EnterVerificationCodeDialog, type VerificationCode } from './EnterVerificationCodeDialog';
import { useSendPhoneVerificationCode } from './useSendPhoneVerificationCode';

interface CollectPhoneNumberDialogsProps extends IDisclosureOptions {
  isOpen: boolean;
  signerEmail: string;
}

export function CollectPhoneNumberDialogs(props: CollectPhoneNumberDialogsProps) {
  const { isOpen, envelopeId, signerId, signerEmail } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const isFullScreen = !!useMediaQuery(theme.breakpoints.down('xs'));

  const [contactInfo, setContactInfo] = React.useState<ISendPhoneVerificationCodeOptions | null>(null);
  const sendCode = useSendPhoneVerificationCode({ signerId, envelopeId });

  async function handleContactInfoEntered(data: ISendPhoneVerificationCodeOptions) {
    setContactInfo(data);
    await sendCode.mutateAsync(data);
  }

  function handleVerificationCodeEntered(data: VerificationCode) {
    if (!contactInfo?.phoneNumber) {
      console.error('Phone number is not set');
      setContactInfo(null);
      return;
    }

    const isCodeValid = sendCode.verify(data.verificationCode);
    if (!isCodeValid) return;

    collectPhoneNumber(contactInfo.phoneNumber);
  }

  function collectPhoneNumber(phoneNumber: string) {
    // Store phone number in the reduxState.modal.signerPhoneNumber
    // so that it can be included in the call to submit envelope
    dispatch(signerPhoneNumberCollected({ phoneNumber, email: signerEmail, signerId }));
  }

  return (
    <>
      {!contactInfo ? (
        <EnterContactInfoDialog
          disablePortal
          fullScreen={isFullScreen}
          isOpen={isOpen}
          onSubmit={handleContactInfoEntered}
        />
      ) : (
        <EnterVerificationCodeDialog
          disablePortal
          fullScreen={isFullScreen}
          isOpen={isOpen}
          onBack={() => setContactInfo(null)}
          onSubmit={handleVerificationCodeEntered}
          resendCode={() => sendCode.mutateAsync(contactInfo)}
          secsUntilResend={sendCode.secsUntilResend}
          sendCodeError={sendCode.error}
        />
      )}
    </>
  );
}
